import React, { Component } from 'react';
import TabbedSidebar from './TabbedSidebar';
import GarmentPreview from './GarmentPreview';
import PatternPicker from './PatternPicker';
import OptionPicker from './OptionPicker';
import LogoPlacer from './LogoPlacer';
import NumberPlacer from './NumberPlacer';
import NamePlacer from './NamePlacer';

import bg from '../../img/bg-3.png';
import sportstarLogo from './sportstar-logo.svg';
// Font file imports — CRA resolves these to their hashed public URLs,
// which we fetch at screenshot time to embed as base64 @font-face rules.
import collegeBlockWoff2 from './CollegeBlock.woff2';
import collegeBlockWoff  from './CollegeBlock.woff';
import jerseyM54Woff2    from './JerseyM54.woff2';
import jerseyM54Woff     from './JerseyM54.woff';

const CMS_URL = process.env.REACT_APP_API_URL || '';

// Ensure originalHex always has a leading '#', regardless of how it was entered in the CMS
const normaliseHex = (hex) => {
    if (!hex) return hex;
    return hex.startsWith('#') ? hex : `#${hex}`;
};

const mapGarmentOptions = (options) =>
    (options || []).map((opt) => ({
        id: opt.id,
        label: opt.label,
        url: opt.url,
        colorSlots: (opt.colorSlots || []).map((slot) => ({
            ...slot,
            originalHex: normaliseHex(slot.originalHex),
        })),
    }));

/**
 * TabbedSidebarDemo
 *
 * Top-level garment builder page. Fetches garment data and patterns from the
 * Payload CMS API on mount, then passes them down to all child components.
 * The garment slug to load is currently hard-coded to 'shorts'; it can be
 * made dynamic (e.g. from a route param) in the future.
 */
class TabbedSidebarDemo extends Component {
    constructor(props) {
        super(props);
        this.garmentPreviewRef = React.createRef();
        this.contentRef = React.createRef();
        this.state = {
            // ── API-fetched data ──────────────────────────────────────────
            garments: [],           // [{ id, slug, name, baseImage }]  — all garments from API
            garment: null,          // full garment document from API (currently selected)
            patterns: [],           // [{ id, label, url }]
            garmentOptions: [],     // [{ id, label, url, colorSlots: [{ originalHex }] }]
            loading: true,
            loadError: null,

            // ── Builder state ─────────────────────────────────────────────
            patternId: null,
            // patternOriginals: ColorSlot[][] discovered per pattern when SVGs are fetched
            patternOriginals: {},   // { [patternId]: ColorSlot[] }
            // globalColors: one shared palette indexed by slot position
            globalColors: [],       // string[]
            stripeIds: [],          // string[]  — all currently active option ids
            stripeColors: {},       // { [stripeId]: string[] }  per-stripe solid chosen colors
            stripePatterns: {},     // { [stripeId]: patternId | null }
            stripePatternColors: {},// { [stripeId]: string[] }
            stripePatternOriginals: {}, // { [stripeId]: { [patternId]: ColorSlot[] } }
            logos: [],
            numbers: [],
            names: [],
        };
    }

    componentDidMount() {
        // Fetch all garments (no slug filter) alongside all patterns
        const garmentsFetch = fetch(
            `${CMS_URL}/api/garments?depth=2&limit=100`
        ).then((r) => r.json());

        const patternsFetch = fetch(
            `${CMS_URL}/api/patterns?limit=100`
        ).then((r) => r.json());

        Promise.all([garmentsFetch, patternsFetch])
            .then(([garmentsJson, patternsJson]) => {
                const garmentDocs = garmentsJson.docs || [];
                const patternDocs = patternsJson.docs || [];

                if (garmentDocs.length === 0) {
                    this.setState({ loading: false, loadError: 'No garments found in CMS.' });
                    return;
                }

                // Summary list used by the garment chooser tab
                const garments = garmentDocs.map((doc) => ({
                    id: doc.id,
                    slug: doc.slug,
                    name: doc.name,
                    baseImage: doc.baseImage || null,
                }));

                // Map patterns API response to the shape child components expect
                const patterns = patternDocs.map((doc) => ({
                    id: doc.slug,
                    label: doc.label,
                    url: doc.url,
                }));

                // Default to the first garment
                const garmentDoc = garmentDocs[0];
                const garmentOptions = mapGarmentOptions(garmentDoc.options);

                // Default to the first pattern's id
                const defaultPatternId = patterns.length > 0 ? patterns[0].id : null;

                this.setState({
                    garments,
                    garment: garmentDoc,
                    patterns,
                    garmentOptions,
                    patternId: defaultPatternId,
                    loading: false,
                });
            })
            .catch((err) => {
                console.error('TabbedSidebarDemo: failed to load CMS data', err);
                this.setState({ loading: false, loadError: 'Failed to load garment data.' });
            });
    }

    // Switch to a different garment — re-fetch its full doc then reset builder state
    handleGarmentSelect = (slug) => {
        const { garment } = this.state;
        if (garment && garment.slug === slug) return; // already selected

        fetch(`${CMS_URL}/api/garments?where[slug][equals]=${slug}&depth=2&limit=1`)
            .then((r) => r.json())
            .then((json) => {
                const garmentDoc = (json.docs || [])[0] || null;
                if (!garmentDoc) return;
                const garmentOptions = mapGarmentOptions(garmentDoc.options);
                this.setState((prev) => ({
                    garment: garmentDoc,
                    garmentOptions,
                    // Reset garment-specific builder state but keep pattern colours —
                    // patterns are shared across garments so their discovered colours
                    // remain valid and the pattern toggle stays functional.
                    patternId: prev.patterns.length > 0 ? prev.patterns[0].id : null,
                    stripeIds: [],
                    stripeColors: {},
                    stripePatterns: {},
                    stripePatternColors: {},
                    stripePatternOriginals: {},
                    logos: [],
                    numbers: [],
                    names: [],
                }));
            })
            .catch((err) => console.error('TabbedSidebarDemo: failed to switch garment', err));
    };

    // Called by GarmentPreview after it fetches + parses each pattern SVG.
    handlePatternColorsDiscovered = (patternId, slots) => {
        this.setState((prev) => {
            const next = [...prev.globalColors];
            slots.forEach((slot, i) => {
                if (next[i] === undefined) next[i] = slot.color;
            });
            return {
                patternOriginals: { ...prev.patternOriginals, [patternId]: slots },
                globalColors: next,
            };
        });
    };

    handlePatternChange = (patternId) => {
        this.setState({ patternId });
    };

    handlePatternColorChange = (patternId, slotIndex, color) => {
        this.setState((prev) => {
            const next = [...prev.globalColors];
            next[slotIndex] = color;
            return { globalColors: next };
        });
    };

    handleStripeToggle = (stripeId) => {
        this.setState((prev) => {
            const ids = prev.stripeIds;
            const next = ids.includes(stripeId)
                ? ids.filter((id) => id !== stripeId)
                : [...ids, stripeId];
            return { stripeIds: next };
        });
    };

    handleStripeColorChange = (stripeId, slotIndex, color) => {
        this.setState((prev) => {
            const existing = prev.stripeColors[stripeId] || [];
            const next = [...existing];
            next[slotIndex] = color;
            return { stripeColors: { ...prev.stripeColors, [stripeId]: next } };
        });
    };

    handleStripePatternChange = (stripeId, patternId) => {
        this.setState((prev) => ({
            stripePatterns: { ...prev.stripePatterns, [stripeId]: patternId || null },
        }));
    };

    handleStripePatternColorChange = (stripeId, slotIndex, color) => {
        this.setState((prev) => {
            const existing = prev.stripePatternColors[stripeId] || [];
            const next = [...existing];
            next[slotIndex] = color;
            return { stripePatternColors: { ...prev.stripePatternColors, [stripeId]: next } };
        });
    };

    handleStripePatternColorsDiscovered = (stripeId, patternId, slots) => {
        this.setState((prev) => {
            const forStripe = prev.stripePatternOriginals[stripeId] || {};
            return {
                stripePatternOriginals: {
                    ...prev.stripePatternOriginals,
                    [stripeId]: { ...forStripe, [patternId]: slots },
                },
            };
        });
    };

    handleLogoAdd    = (logo) => this.setState((prev) => ({ logos: [...prev.logos, logo] }));
    handleLogoRemove = (id)   => this.setState((prev) => ({ logos: prev.logos.filter((l) => l.id !== id) }));
    handleLogoUpdate = (id, patch) => this.setState((prev) => ({
        logos: prev.logos.map((l) => l.id === id ? { ...l, ...patch } : l),
    }));

    handleNumberAdd    = (num) => this.setState((prev) => ({ numbers: [...prev.numbers, num] }));
    handleNumberRemove = (id)  => this.setState((prev) => ({ numbers: prev.numbers.filter((n) => n.id !== id) }));
    handleNumberUpdate = (id, patch) => this.setState((prev) => ({
        numbers: prev.numbers.map((n) => n.id === id ? { ...n, ...patch } : n),
    }));

    handleNameAdd    = (name) => this.setState((prev) => ({ names: [...prev.names, name] }));
    handleNameRemove = (id)   => this.setState((prev) => ({ names: prev.names.filter((n) => n.id !== id) }));
    handleNameUpdate = (id, patch) => this.setState((prev) => ({
        names: prev.names.map((n) => n.id === id ? { ...n, ...patch } : n),
    }));

    // Converts any URL (blob:, relative, http:) to a data: URI.
    _urlToDataUri(url) {
        return fetch(url)
            .then((r) => r.blob())
            .then(
                (blob) =>
                    new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    })
            );
    }

    takeScreenshot = async () => {
        const svgEl = this.garmentPreviewRef.current &&
            this.garmentPreviewRef.current.svgRef &&
            this.garmentPreviewRef.current.svgRef.current;
        if (!svgEl) return;

        // ── Native garment canvas at full 1650×1550 resolution ──────────
        const W = 1650;
        const H = 1550;
        const canvas = document.createElement('canvas');
        canvas.width  = W;
        canvas.height = H;
        const ctx = canvas.getContext('2d');

        // ── Step 1: inline all @font-face rules into the SVG clone ────────
        // The browser Canvas cannot use document-loaded @font-face fonts when
        // rendering an SVG-as-image; we must embed them as base64 data URIs.
        let fontCss = '';
        try {
            const [cb2, cb, jm2, jm] = await Promise.all([
                this._urlToDataUri(collegeBlockWoff2),
                this._urlToDataUri(collegeBlockWoff),
                this._urlToDataUri(jerseyM54Woff2),
                this._urlToDataUri(jerseyM54Woff),
            ]);
            fontCss = `
@font-face {
  font-family: 'CollegeBlock';
  src: url('${cb2}') format('woff2'), url('${cb}') format('woff');
  font-weight: normal; font-style: normal;
}
@font-face {
  font-family: 'JerseyM54';
  src: url('${jm2}') format('woff2'), url('${jm}') format('woff');
  font-weight: normal; font-style: normal;
}`;
        } catch (e) {
            console.warn('TabbedSidebarDemo: could not inline fonts for screenshot', e);
        }

        // ── Step 2: clone SVG and inline all image hrefs as data URIs ─────
        const svgClone = svgEl.cloneNode(true);

        // Force the viewBox to the full native size and remove any CSS sizing
        svgClone.setAttribute('viewBox', `0 0 ${W} ${H}`);
        svgClone.setAttribute('width',  String(W));
        svgClone.setAttribute('height', String(H));
        svgClone.style.cssText = '';

        // Embed font declarations
        if (fontCss) {
            const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');
            styleEl.textContent = fontCss;
            svgClone.insertBefore(styleEl, svgClone.firstChild);
        }

        // Inline every <image href>
        const images = svgClone.querySelectorAll('image');
        await Promise.all(
            Array.from(images).map(async (img) => {
                const href = img.getAttribute('href') || img.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
                if (!href || href.startsWith('data:') || href.startsWith('blob:')) {
                    // blob: URLs (recolored pattern/stripe blobs) also need inlining
                    if (href && href.startsWith('blob:')) {
                        try {
                            img.setAttribute('href', await this._urlToDataUri(href));
                        } catch (e) {
                            console.warn('TabbedSidebarDemo: could not inline blob href', href, e);
                        }
                    }
                    return;
                }
                try {
                    img.setAttribute('href', await this._urlToDataUri(href));
                } catch (e) {
                    console.warn('TabbedSidebarDemo: could not inline image href', href, e);
                }
            })
        );

        // ── Step 3: serialise the SVG and draw onto canvas ────────────────
        const serializer = new XMLSerializer();
        const svgStr = serializer.serializeToString(svgClone);
        const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
        const svgBlobUrl = URL.createObjectURL(svgBlob);

        await new Promise((resolve) => {
            const img = new Image(W, H);
            img.onload = () => {
                ctx.drawImage(img, 0, 0, W, H);
                URL.revokeObjectURL(svgBlobUrl);
                resolve();
            };
            img.onerror = () => { URL.revokeObjectURL(svgBlobUrl); resolve(); };
            img.src = svgBlobUrl;
        });

        // ── Step 4: trigger download ──────────────────────────────────────
        const garmentSlug = this.state.garment ? this.state.garment.slug : 'garment';
        const link = document.createElement('a');
        link.download = `${garmentSlug}-design.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    render() {
        const {
            garments, garment, patterns, garmentOptions, loading, loadError,
            patternId, patternOriginals, globalColors,
            stripeIds, stripeColors,
            stripePatterns, stripePatternColors, stripePatternOriginals,
        } = this.state;

        if (loading) {
            return (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    background: '#121212',
                    color: '#888',
                    fontSize: '14px',
                }}>
                    Loading…
                </div>
            );
        }

        if (loadError) {
            return (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    background: '#121212',
                    color: '#ff5050',
                    fontSize: '14px',
                }}>
                    {loadError}
                </div>
            );
        }

        // Clip paths from garment document (fallback if no clipMaskImage)
        const clipPaths = (garment && garment.clipPaths) ? garment.clipPaths : [];

        // Base/shader image URLs from the expanded garment document
        const baseImageUrl    = garment && garment.baseImage    ? garment.baseImage.url    : null;
        const shaderImageUrl  = garment && garment.shaderImage  ? garment.shaderImage.url  : null;
        const clipMaskUrl     = garment && garment.clipMaskImage ? garment.clipMaskImage.url : null;
        const imageTransform  = (garment && garment.imageTransform) || null;

        // Slice globalColors to the slot count of the active pattern for GarmentPreview
        const activeSlots = (patternOriginals[patternId] || []);
        const activePatternColors = activeSlots.map((_, i) => globalColors[i] || _.color);

        const tabs = [
            {
                id: 1,
                icon: '①',
                label: 'Garment',
                content: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{
                            fontSize: '11px',
                            color: '#888',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            marginBottom: '4px',
                        }}>
                            Choose Garment
                        </div>
                        {garments.map((g) => {
                            const isActive = garment && garment.slug === g.slug;
                            return (
                                <button
                                    key={g.slug}
                                    onClick={() => this.handleGarmentSelect(g.slug)}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '12px 8px',
                                        background: isActive ? '#2a2a2a' : '#1a1a1a',
                                        border: isActive ? '2px solid #ecff19' : '2px solid #333',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'border-color 0.15s',
                                        width: '100%',
                                    }}
                                >
                                    {g.baseImage && g.baseImage.url && (
                                        <img
                                            src={`${CMS_URL}${g.baseImage.url}`}
                                            alt={g.name}
                                            style={{
                                                width: '80px',
                                                height: '80px',
                                                objectFit: 'contain',
                                                display: 'block',
                                                opacity: isActive ? 1 : 0.6,
                                            }}
                                        />
                                    )}
                                    <span style={{
                                        color: isActive ? '#ecff19' : '#aaa',
                                        fontSize: '11px',
                                        fontWeight: isActive ? 600 : 400,
                                        textAlign: 'center',
                                    }}>
                                        {g.name}
                                    </span>
                                    {isActive && (
                                        <span style={{ fontSize: '8px', color: '#ecff19', lineHeight: 1 }}>✓</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                ),
            },
            {
                id: 2,
                icon: '②',
                label: 'Pattern',
                content: (
                    <PatternPicker
                        patterns={patterns}
                        selectedPatternId={patternId}
                        patternOriginals={patternOriginals}
                        globalColors={globalColors}
                        onPatternChange={this.handlePatternChange}
                        onColorChange={this.handlePatternColorChange}
                    />
                ),
            },
            {
                id: 3,
                icon: '③',
                label: 'Stripes',
                content: (
                    <OptionPicker
                        options={garmentOptions}
                        patterns={patterns}
                        selectedStripeIds={stripeIds}
                        stripeColors={stripeColors}
                        stripePatterns={stripePatterns}
                        stripePatternColors={stripePatternColors}
                        stripePatternOriginals={stripePatternOriginals}
                        onStripeToggle={this.handleStripeToggle}
                        onStripeColorChange={this.handleStripeColorChange}
                        onStripePatternChange={this.handleStripePatternChange}
                        onStripePatternColorChange={this.handleStripePatternColorChange}
                        onStripePatternColorsDiscovered={this.handleStripePatternColorsDiscovered}
                    />
                ),
            },
            {
                id: 4,
                icon: '④',
                label: 'Logos',
                content: (
                    <LogoPlacer
                        logos={this.state.logos}
                        onAdd={this.handleLogoAdd}
                        onRemove={this.handleLogoRemove}
                        onUpdate={this.handleLogoUpdate}
                    />
                ),
            },
            {
                id: 5,
                icon: '⑤',
                label: 'Numbers',
                content: (
                    <NumberPlacer
                        numbers={this.state.numbers}
                        onAdd={this.handleNumberAdd}
                        onRemove={this.handleNumberRemove}
                        onUpdate={this.handleNumberUpdate}
                    />
                ),
            },
            {
                id: 6,
                icon: '⑥',
                label: 'Names',
                content: (
                    <NamePlacer
                        names={this.state.names}
                        onAdd={this.handleNameAdd}
                        onRemove={this.handleNameRemove}
                        onUpdate={this.handleNameUpdate}
                    />
                ),
            },
            { id: 7,  icon: '⑦', label: 'Materials', content: <div style={{ color: '#888' }}>Materials</div> },
            { id: 8,  icon: '⑧', label: 'Brands',    content: <div style={{ color: '#888' }}>Brands</div> },
            { id: 9,  icon: '⑨', label: 'Tags',      content: <div style={{ color: '#888' }}>Tags</div> },
            { id: 10, icon: '⑩', label: 'Settings',  content: <div style={{ color: '#888' }}>Settings</div> },
        ];

        return (
            <TabbedSidebar tabs={tabs} defaultTab={1} contentRef={this.contentRef}>
                {/* ── Branded header bar ── */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 20px',
                    pointerEvents: 'none',
                    zIndex: 1,
                }}>
                    <img
                        src={sportstarLogo}
                        alt="Sportstar"
                        style={{ height: '70px', display: 'block' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', pointerEvents: 'auto' }}>
                        <a
                            href="mailto:sales@sportstar.co"
                            style={{
                                color: '#ecff19',
                                fontSize: '12px',
                                fontWeight: 600,
                                textDecoration: 'none',
                                letterSpacing: '0.03em',
                            }}
                        >
                            Contact: sales@sportstar.co
                        </a>
                        <button
                            className="screenshot-btn"
                            onClick={this.takeScreenshot}
                            title="Download PNG"
                        >
                            ↓ Download PNG
                        </button>
                    </div>
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        padding: '24px',
                    }}
                >
                    <GarmentPreview
                        ref={this.garmentPreviewRef}
                        baseImageUrl={baseImageUrl}
                        shaderImageUrl={shaderImageUrl}
                        imageTransform={imageTransform}
                        clipMaskUrl={clipMaskUrl}
                        clipPaths={clipPaths}
                        patterns={patterns}
                        options={garmentOptions}
                        patternId={patternId}
                        patternOriginals={patternOriginals}
                        patternColors={activePatternColors}
                        stripeIds={stripeIds}
                        stripeColors={stripeColors}
                        stripePatterns={stripePatterns}
                        stripePatternColors={stripePatternColors}
                        stripePatternOriginals={stripePatternOriginals}
                        logos={this.state.logos}
                        numbers={this.state.numbers}
                        names={this.state.names}
                        onPatternColorsDiscovered={this.handlePatternColorsDiscovered}
                        onStripePatternColorsDiscovered={this.handleStripePatternColorsDiscovered}
                    />
                </div>
                <style jsx>
                    {`
                        .sidebar-content {
                            background-image: url(${bg});
                            background-color: ${activePatternColors[0]};
                        }
                    `}
                </style>
            </TabbedSidebar>
        );
    }
}

export default TabbedSidebarDemo;
